"use client";

/**
 * Use-case demo card — links a visitor through to a use-case page. Shows the
 * quiet short preview only when that short is published AND the autoplay
 * rules allow it (see VideoPreview); otherwise the card is a plain, fully
 * informative text card. Nothing on the card is hover-only.
 */

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { trackVideo } from "./video-analytics";
import { VideoPreview } from "./video-preview";
import { getMarketingVideo, isPlayable, type MarketingVideoId } from "@/lib/marketing-videos";

export function VideoCard({
  href,
  title,
  description,
  cta,
  icon,
  previewId,
}: {
  href: string;
  title: string;
  description: string;
  cta: string;
  icon: React.ReactNode;
  /** SHORT manifest variant used as quiet preview when published. */
  previewId?: MarketingVideoId;
}) {
  const preview = previewId ? getMarketingVideo(previewId) : null;
  const showPreview = preview !== null && isPlayable(preview);
  return (
    <Link
      href={href}
      onClick={() => {
        if (preview) trackVideo(preview.analyticsName, "cta");
      }}
      className="group flex h-full flex-col rounded-2xl border border-line-subtle bg-bg-raised p-6 transition hover:border-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
    >
      {showPreview && previewId ? (
        <div className="mb-5">
          <VideoPreview id={previewId} />
        </div>
      ) : (
        <span className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-lg border border-line-subtle bg-bg-elev text-brand-300">
          {icon}
        </span>
      )}
      <h3 className="text-[16px] font-semibold text-fg-bright leading-snug">{title}</h3>
      <p className="mt-2 flex-1 text-[13.5px] leading-relaxed text-fg-dim">{description}</p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-[13.5px] font-medium text-brand-300 transition group-hover:text-brand-200">
        {cta}
        <ArrowRight size={14} />
      </span>
    </Link>
  );
}
